<template>
  <tr v-for="(row, rowIndex) in category.rows" :key="`${category.name}-${rowIndex}`" 
      :class="rowIndex % 2 === 0 ? 'bg-slate-50' : 'bg-white'">
    <td v-if="rowIndex === 0" :rowspan="category.rows.length" 
        class="px-3 py-2 text-xs font-semibold text-slate-800 border-r border-slate-200 align-top bg-slate-50 relative">
      <div class="flex items-center justify-between">
        <div class="flex items-center justify-between flex-1">
          <div v-if="isEditing" class="flex items-center space-x-1 flex-1">
            <input
              ref="labelInput"
              v-model="editingName"
              @keyup.enter="saveLabel"
              @keyup.esc="cancelEdit"
              class="flex-1 px-2 py-1 text-xs font-semibold text-slate-800 border border-slate-300 rounded focus:ring-1 focus:ring-blue-500 focus:border-blue-500 bg-white"
              placeholder="Enter label name"
              autofocus
            />
            <button
              @click="saveLabel"
              class="px-2 py-1 text-xs text-green-600 hover:text-green-800 hover:bg-green-50 rounded transition-colors"
              title="Confirm edit"
            >
              <i class="fas fa-check"></i>
            </button>
            <button
              @click="cancelEdit"
              class="px-2 py-1 text-xs text-red-600 hover:text-red-800 hover:bg-red-50 rounded transition-colors"
              title="Cancel edit"
            >
              <i class="fas fa-times"></i>
            </button>
          </div>
          <div v-else class="flex items-center justify-between flex-1">
            <span 
              @click="startEdit"
              class="cursor-pointer hover:text-blue-600 transition-colors"
              :title="category.name === 'Manual' ? '' : 'Click to edit'"
            >
              {{ category.name }}
            </span>
            <button
              v-if="category.name !== 'Manual'"
              @click="deleteLabel"
              class="ml-2 text-red-500 hover:text-red-700 transition-colors"
              title="Delete label"
            >
              <i class="fas fa-trash text-xs"></i>
            </button>
          </div>
        </div>
      </div>
    </td>
    <td class="px-3 py-2 text-xs text-slate-700 border-r border-slate-200 font-medium">
      {{ row.games }}
    </td>
    <td class="px-2 py-1 border-r border-slate-200">
      <input
        v-model.number="row.lastSeason" 
        type="number" 
        step="0.01" 
        min="0" 
        max="1"
        class="w-full px-2 py-1 text-xs text-center border border-slate-300 rounded focus:ring-1 focus:ring-blue-500 focus:border-blue-500 bg-white hover:bg-slate-50 transition-colors"
      />
    </td>
    <td class="px-2 py-1 border-r border-slate-200">
      <input 
        v-model.number="row.twoSeasonsAgo" 
        type="number" 
        step="0.01" 
        min="0" 
        max="1"
        class="w-full px-2 py-1 text-xs text-center border border-slate-300 rounded focus:ring-1 focus:ring-blue-500 focus:border-blue-500 bg-white hover:bg-slate-50 transition-colors"
      />
    </td>
    <td class="px-2 py-1 border-r border-slate-200">
      <input 
        v-model.number="row.threeSeasonsAgo" 
        type="number" 
        step="0.01" 
        min="0" 
        max="1"
        class="w-full px-2 py-1 text-xs text-center border border-slate-300 rounded focus:ring-1 focus:ring-blue-500 focus:border-blue-500 bg-white hover:bg-slate-50 transition-colors"
      />
    </td>
    <td class="px-2 py-1 border-r border-slate-200">
      <input 
        v-model.number="row.leagueAvg" 
        type="number" 
        step="0.01" 
        min="0" 
        max="1"
        class="w-full px-2 py-1 text-xs text-center border border-slate-300 rounded focus:ring-1 focus:ring-blue-500 focus:border-blue-500 bg-white hover:bg-slate-50 transition-colors"
      />
    </td>
    <td class="px-2 py-1 border-r border-slate-200">
      <input 
        v-model.number="row.lastFiveGames" 
        type="number" 
        step="0.01" 
        min="0" 
        max="1"
        class="w-full px-2 py-1 text-xs text-center border border-slate-300 rounded focus:ring-1 focus:ring-blue-500 focus:border-blue-500 bg-white hover:bg-slate-50 transition-colors"
      />
    </td>
    <td class="px-2 py-1">
      <input 
        v-model.number="row.restOfSeason" 
        type="number" 
        step="0.01" 
        min="0" 
        max="1"
        class="w-full px-2 py-1 text-xs text-center border border-slate-300 rounded focus:ring-1 focus:ring-blue-500 focus:border-blue-500 bg-white hover:bg-slate-50 transition-colors"
      />
    </td>
  </tr>
</template>

<script>
export default {
  name: 'ProjectionLabelRow',
  emits: ['update-label-name', 'delete-label'],
  props: {
    category: {
      type: Object,
      required: true
    },
    categoryIndex: {
      type: Number,
      required: true
    },
    allLabels: {
      type: Array,
      required: true
    }
  },
  data() {
    return {
      isEditing: false,
      editingName: ''
    }
  },
  methods: {
    startEdit() {
      this.editingName = this.category.name
      this.isEditing = true
    },
    
    saveLabel() {
      if (this.editingName.trim()) {
        // Check if label name already exists (except for current label)
        const existingLabel = this.allLabels.find((cat, index) => 
          index !== this.categoryIndex && cat.name.toLowerCase() === this.editingName.trim().toLowerCase()
        )
        
        if (existingLabel) {
          alert(`Label "${this.editingName.trim()}" already exists. Please choose a different name.`)
          return
        }
        
        // Emit the updated name to parent
        this.$emit('update-label-name', this.categoryIndex, this.editingName.trim())
        this.isEditing = false
      } else {
        alert('Label name cannot be empty.')
      }
    },
    
    cancelEdit() {
      if (this.category.name === 'New Label') {
        // If it's a new label and user cancels, remove it
        this.$emit('delete-label', this.categoryIndex)
      } else {
        this.isEditing = false
        this.editingName = this.category.name
      }
    },
    
    deleteLabel() {
      if (confirm(`Are you sure you want to delete the label "${this.category.name}"?`)) {
        this.$emit('delete-label', this.categoryIndex)
      }
    }
  }
}
</script>
